import { useState } from 'react'
import type { OpenAction } from '../api/actions'
import { formatDateBR } from '../lib/format'
import { PriorityBadge } from './PriorityBadge'
import { ActionIcons } from './ActionIcons'
import { ActionComment, ActionStateBadge } from './ActionStateView'
import { ActionQuickEditModal } from './ActionQuickEditModal'

const PRIORITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 }

/** Etiqueta de origem da ação (otimização vs. derivada de investigação). */
function sourceBadge(a: OpenAction): { label: string; className: string } {
  if (a.type === 'derived' && a.investigationType === 'ai_auto') {
    return { label: 'Análise Aprofundada', className: 'bg-violet-100 text-violet-700' }
  }
  if (a.type === 'derived' && a.investigationType === 'manager_manual') {
    return { label: 'Análise do gerente', className: 'bg-sky-100 text-sky-700' }
  }
  if (a.type === 'derived') {
    return { label: 'Análise Aprofundada', className: 'bg-violet-100 text-violet-700' }
  }
  return { label: 'Otimização', className: 'bg-slate-100 text-slate-600' }
}

interface ClientGroup {
  clientId: string
  clientName: string
  actions: OpenAction[]
}
interface Level1Group {
  key: string
  label: string
  sortVal: string
  clients: Map<string, ClientGroup>
  total: number
}

/** Agrupa as ações abertas em 2 níveis: gerente OU semana (nível 1) → cliente (nível 2). */
export function OpenActionsGroups({
  actions,
  groupBy,
}: {
  actions: OpenAction[]
  groupBy: 'manager' | 'week'
}) {
  const [editing, setEditing] = useState<OpenAction | null>(null)
  const [showComments, setShowComments] = useState(false)
  const level1 = new Map<string, Level1Group>()
  for (const a of actions) {
    const key = groupBy === 'manager' ? a.managerId : a.weekStart
    const label =
      groupBy === 'manager' ? a.managerName : `Semana de ${formatDateBR(a.weekStart)}`
    const sortVal = groupBy === 'manager' ? a.managerName : a.weekStart
    let g = level1.get(key)
    if (!g) {
      g = { key, label, sortVal, clients: new Map(), total: 0 }
      level1.set(key, g)
    }
    g.total++
    let cl = g.clients.get(a.clientId)
    if (!cl) {
      cl = { clientId: a.clientId, clientName: a.clientName, actions: [] }
      g.clients.set(a.clientId, cl)
    }
    cl.actions.push(a)
  }

  const groups = [...level1.values()].sort((x, y) =>
    groupBy === 'week' ? y.sortVal.localeCompare(x.sortVal) : y.total - x.total
  )

  if (groups.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
        Nenhuma ação aberta. 🎉
      </p>
    )
  }

  return (
    <>
    <div className="space-y-5">
      <div className="flex items-center justify-end">
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={showComments}
            onChange={(e) => setShowComments(e.target.checked)}
            className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          Mostrar comentários
        </label>
      </div>
      {groups.map((g) => (
        <section key={g.key} className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-900">{g.label}</h2>
            <span className="rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-semibold text-brand-700">
              {g.total} {g.total === 1 ? 'ação' : 'ações'}
            </span>
          </div>
          <div className="space-y-4">
            {[...g.clients.values()]
              .sort((a, b) =>
                a.clientName.localeCompare(b.clientName, 'pt-BR', { sensitivity: 'base' })
              )
              .map((c) => (
              <div key={c.clientId}>
                <h3 className="mb-1.5 text-sm font-semibold text-slate-700">{c.clientName}</h3>
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-100">
                  {c.actions
                    .slice()
                    // Mais recentes primeiro; na mesma semana, pela prioridade.
                    .sort(
                      (a, b) =>
                        b.weekStart.localeCompare(a.weekStart) ||
                        PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]
                    )
                    .map((a) => (
                      <li key={a.id} className="flex items-start gap-3 px-3 py-2">
                        <PriorityBadge priority={a.priority} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-slate-800">{a.description}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                sourceBadge(a).className
                              }`}
                            >
                              {sourceBadge(a).label}
                            </span>
                            <ActionStateBadge state={a.state} />
                            {groupBy === 'manager' && (
                              <span className="text-xs text-slate-400">
                                semana {formatDateBR(a.weekStart)}
                              </span>
                            )}
                          </div>
                          {showComments && <ActionComment state={a.state} className="mt-1" />}
                        </div>
                        <ActionIcons reportId={a.reportId} onEdit={() => setEditing(a)} />
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
    {editing && (
      <ActionQuickEditModal action={editing} onClose={() => setEditing(null)} />
    )}
    </>
  )
}
